import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isNil } from 'lodash/fp';
import { Box, Button, DialogActions, DialogContent, DialogTitle } from '@material-ui/core';
import { usePriceService } from '../../../../../../utils/hooks/priceService';
import { useUser } from '../../../../../../utils/hooks/auth';
import { Dialog, FullScreenDialog, Typography } from '../../../../../../ui-kit';
import { CreateWarehouseCardMutation, UnitType } from '../../../../../../generated/graphql';
import { useWarehousesScope } from '../../../../../warehouse/providers/WarehouseScopeProvider';
import DeliveryListCreateWarehouseForm from '../../../DeliveryListCreateWarehouseForm';
import { StockCardSearch } from '../common/StockCardSearch';
import { PendingListItemFormValue, StockCard } from '../../types';

interface AttachDialogProps {
    isOpened: boolean;
    item?: PendingListItemFormValue | null;
    onClose: () => void;
    onAttach: (card: StockCard) => void;
}

const AttachDialogProps = ({ isOpened, item, onClose, onAttach }: AttachDialogProps) => {
    const { t } = useTranslation();
    const { priceService } = usePriceService();
    const { user: { defaultCurrency } } = useUser();
    const { currentWarehouseId } = useWarehousesScope();

    const [isCreateWarehouseCardDialogOpen, setIsCreateWarehouseCardDialogOpen] = useState(false);
    const [selectedStockCard, setSelectedStockCard] = useState<StockCard | null>(null);

    const handleCardSelected = (card: StockCard) => {
        setSelectedStockCard(card);
    };

    const handleClose = () => {
        setSelectedStockCard(null);
        onClose();
    };

    const handleConfirm = () => {
        if (selectedStockCard) {
            onAttach(selectedStockCard);
        }
        handleClose();
    };

    const handleWarehouseCardCreate = ({ createStockCard }: CreateWarehouseCardMutation) => {
        setIsCreateWarehouseCardDialogOpen(false);
        onAttach(createStockCard as unknown as StockCard);
        handleClose();
    };

    let newWarehouseCardFormInitialValues;
    if (item) {
        let priceWithoutVat = item.unitPriceWithoutVat;
        if (!isNil(priceWithoutVat)) {
            priceWithoutVat = priceService.integerToFloatWithPrecision(priceWithoutVat, defaultCurrency.precision);
        }

        newWarehouseCardFormInitialValues = {
            name: item.description || undefined,
            sku: item.code || undefined,
            priceWithoutVat: priceWithoutVat || undefined,
            storageUnitId: item.unitOfMeasure || UnitType.Pcs,
        };
    }

    return (
        <>
            <Dialog isOpened={isOpened} onClose={handleClose} fullWidth>
                <DialogTitle>
                    {t('Merge to a stock card')}
                </DialogTitle>
                <DialogContent>
                    <Typography variant='body1'>
                        {t('Pick a card to merge with')}
                        {' '}
                        {item?.description}
                    </Typography>
                    <Box mt={2} />
                    <StockCardSearch
                        isSingleSelect
                        onAdd={handleCardSelected}
                        onCreateNewClick={() => setIsCreateWarehouseCardDialogOpen(true)}
                        pickedStockCards={[]}
                        passedSearchValue={selectedStockCard?.name}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose}>{t('Cancel')}</Button>
                    <Button color='primary' variant='contained' onClick={handleConfirm} disabled={!selectedStockCard}>{t('Attach')}</Button>
                </DialogActions>
            </Dialog>
            <FullScreenDialog isOpened={isCreateWarehouseCardDialogOpen}>
                <DeliveryListCreateWarehouseForm
                    dialogName={t('New warehouse card')}
                    onClose={() => setIsCreateWarehouseCardDialogOpen(false)}
                    onMutationFinished={handleWarehouseCardCreate}
                    requiredWarehouseIds={[currentWarehouseId as string]}
                    initialValues={newWarehouseCardFormInitialValues}
                />
            </FullScreenDialog>
        </>
    );
};

export default AttachDialogProps;
